import type { User } from "@/features/auth/api/users";
import { cn } from "@/utils/cn";
import { useState } from "react";

const userInitials = (user: User): string => {
  if (user.full_name) {
    return user.full_name
      .split(" ")
      .map((word: string) => word[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }
  return (user.email?.[0] || "?").toUpperCase();
}

export const Avatar = ({
  user,
  className,
  size = "md",
  initialsOnly = false,
}: {
  user: User;
  className?: string;
  size?: "sm" | "md" | "lg";
  /** Always render the name acronym, ignoring any photo_url. */
  initialsOnly?: boolean;
}) => {
  const [imgError, setImgError] = useState(false);

  const sizeClasses = {
    sm: "h-8 w-8 text-sm",
    md: "h-11 w-11 text-lg",
    lg: "h-14 w-14 text-2xl",
  };

  if (!initialsOnly && user.photo_url && !imgError) {
    return (
      <img
        src={user.photo_url}
        alt={user.full_name || user.email || "User"}
        className={cn(
          "rounded-full object-cover shrink-0",
          sizeClasses[size],
          className,
        )}
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-zinc-200 font-display text-zinc-600 leading-none [font-synthesis:weight]",
        sizeClasses[size],
        className,
      )}
    >
      {userInitials(user)}
    </div>
  );
};
